import React, { useState } from 'react';
import { Container, Grid, List, ListItemButton, ListItemText, Typography, Box, Snackbar, Alert, Paper, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import InfoIcon from '@mui/icons-material/Info';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import FaceIcon from '@mui/icons-material/Face';
import PaymentIcon from '@mui/icons-material/Payment';
import ProfileEditForm from '../components/setting/ProfileEditForm';
import AccountSettingsForm from '../components/setting/AccountSettingsForm';
import PrivacySettingsForm from '../components/setting/PrivacySettingsForm';
import { getUserProfile, updateUserProfile } from '../services/api';
import { UserProfile } from '../types/UserProfile';
import StatusLimitsDisplay from '../components/setting/StatusLimitsDisplay';
import AvatarSettingsForm from '../components/setting/AvatarSettingsForm';
import SteamSettings from '../components/profile/SteamSettings';
import XboxSettings from '../components/profile/XboxSettings';
import PSNSettings from '../components/profile/PSNSettings';
import SubscriptionSettings from '../components/setting/SubscriptionSettings';
import { useUser } from '../contexts/UserContext';

const ProfileSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser } = useUser();
  const [activeTab, setActiveTab] = useState('プロフィール');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [isEarlyUser, setIsEarlyUser] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  React.useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await getUserProfile();
      setProfile(response.data);
      setIsEarlyUser(response.data.user_id >= 1 && response.data.user_id <= 3000);
      setIsAdmin(response.data.user_id <= 15);
    } catch (error) {
      console.error('プロフィールの取得に失敗しました', error);
      showSnackbar('プロフィールの取得に失敗しました', 'error');
    }
  };

  const handleSave = async (updatedProfile: UserProfile) => {
    try {
      await updateUserProfile(updatedProfile);
      setProfile(updatedProfile);
      // グローバルキャッシュを更新
      await refreshUser();
      showSnackbar('プロフィールが正常に更新されました', 'success');
    } catch (error: any) {
      console.error('プロフィールの更新に失敗しました', error);
      showSnackbar('プロフィールの更新に失敗しました', 'error');
      throw error;
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleAvatarUpdate = async (newAvatarUrl: string) => {
    if (profile) {
      setProfile({
        ...profile,
        avatar: newAvatarUrl
      });
      // グローバルキャッシュを更新
      await refreshUser();
      showSnackbar('アバターが正常に更新されました', 'success');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'プロフィール':
        return profile ? (
          <ProfileEditForm profile={profile} onSave={handleSave} />
        ) : (
          <Typography>読み込み中...</Typography>
        );
      case 'アバター':
        return profile ? (
          <AvatarSettingsForm 
            currentAvatar={profile.avatar} 
            onAvatarUpdate={handleAvatarUpdate} 
          />
        ) : (
          <Typography>読み込み中...</Typography>
        );
      case 'アカウント':
        return profile ? (
          <AccountSettingsForm profile={profile} onSave={handleSave} />
        ) : (
          <Typography>読み込み中...</Typography>
        );
      case 'プライバシー':
        return profile ? (
          <PrivacySettingsForm profile={profile} onSave={handleSave} />
        ) : (
          <Typography>読み込み中...</Typography>
        );
      case 'サブスクリプション':
        return <SubscriptionSettings />;
      case 'Steam':
        return <SteamSettings />;
      case 'Xbox':
        return <XboxSettings />;
      case 'PlayStation':
        return <PSNSettings isEarlyUser={isEarlyUser} />;
      case 'インフォメーション':
        return profile ? (
          <StatusLimitsDisplay profile={profile} />
        ) : (
          <Typography>読み込み中...</Typography>
        );
      case 'appearance':
        return <Typography>外観設定（未実装）</Typography>;
      case 'accessibility':
        return <Typography>アクセシビリティ設定（未実装）</Typography>;
      case 'notifications':
        return <Typography>通知設定（未実装）</Typography>;
      default:
        return null;
    }
  };

  const getImplementedTabs = () => {
    const baseTabs = ['プロフィール', 'アバター', 'アカウント', 'プライバシー', 'インフォメーション', 'Steam', 'Xbox'];
    const adminTabs = ['PlayStation', 'サブスクリプション'];
    
    return [...baseTabs, ...(isAdmin ? adminTabs : [])];
  };

  const getTabIcon = (tab: string) => {
    switch (tab) {
      case 'プロフィール':
        return <PersonIcon fontSize="small" />;
      case 'アバター':
        return <FaceIcon fontSize="small" />;
      case 'アカウント':
        return <AccountCircleIcon fontSize="small" />;
      case 'プライバシー':
        return <LockIcon fontSize="small" />;
      case 'サブスクリプション':
        return <PaymentIcon fontSize="small" />;
      case 'Steam':
        return <img src="/icons/service/steam.svg" alt="Steam" style={{ width: '21.42px', height: '21.42px', padding: '0.5px' }} />;
      case 'PlayStation':
        return <img src="/icons/service/playstation_color.svg" alt="PlayStation" style={{ width: '21.42px', height: '21.42px', padding: '0px' }} />;
      case 'Xbox':
        return <img src="/icons/service/xbox_color.svg" alt="Xbox" style={{ width: '21.42px', height: '21.42px', padding: '1px' }} />;
      case 'インフォメーション':
        return <InfoIcon fontSize="small" />;
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, p: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton 
          onClick={() => navigate('/profile')}
          size="small"
          sx={{ mr: 1.5, color: 'text.secondary' }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Setting
        </Typography>
      </Box>
      <Paper elevation={0} sx={{ bgcolor: 'background.paper' }}>
        <Grid container>
          <Grid item xs={12} md={3} sx={{ borderRight: 1, borderColor: 'divider' }}>
            <List component="nav" sx={{ py: 0 }}>
              {getImplementedTabs().map((tab) => (
                <ListItemButton
                  key={tab}
                  selected={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                  sx={{
                    py: { xs: 0.5, sm: 1 },
                    borderLeft: activeTab === tab ? 2 : 0,
                    borderColor: tab === 'Steam' ? '#285ea0' : tab === 'Xbox' ? '#6bb96b' : tab === 'PlayStation' ? '#0070d1' : 'primary.main',
                    '&.Mui-selected': {
                      backgroundColor: tab === 'Steam' ? '#1b2838' : tab === 'Xbox' ? 'rgba(107, 185, 107, 0.1)' : tab === 'PlayStation' ? 'rgba(0, 112, 209, 0.1)' : 'action.selected',
                      '&:hover': {
                        backgroundColor: tab === 'Steam' ? '#1b2838' : tab === 'Xbox' ? 'rgba(107, 185, 107, 0.15)' : tab === 'PlayStation' ? 'rgba(0, 112, 209, 0.15)' : 'action.hover',
                      }
                    },
                    '&:hover': {
                      backgroundColor: tab === 'Steam' ? '#1b2838' : tab === 'Xbox' ? 'rgba(107, 185, 107, 0.15)' : tab === 'PlayStation' ? 'rgba(0, 112, 209, 0.15)' : 'action.hover',
                    },
                    '& .MuiTouchRipple-child': {
                      backgroundColor: tab === 'Steam' ? '#66c0f4' : tab === 'Xbox' ? '#6bb96b' : tab === 'PlayStation' ? '#0070d1' : undefined
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getTabIcon(tab)}
                    <ListItemText 
                      primary={tab.charAt(0).toUpperCase() + tab.slice(1)} 
                      primaryTypographyProps={{
                        fontSize: { xs: 14, sm: 14 },
                        fontWeight: activeTab === tab ? 'bold' : 'normal',
                        ml: 1
                      }}
                    />
                  </Box>
                </ListItemButton>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={9}>
            <Box sx={{ p: 3 }}>
              {renderTabContent()}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfileSettingsPage;
